import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LearnerFormResponse, LearnerFormUpdateRequest } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase SC-22 — the tenant's resolved learner form (`GET /me/learner-form`).
 *
 * The response is already merged server-side: vertical defaults + the
 * institution's overrides, sorted by `order`. The add/edit learner sheet
 * renders straight from it and the BE enforces the same rules on save.
 */
export const learnerFormKeys = {
  all: ["learner-form"] as const,
  me: () => [...learnerFormKeys.all, "me"] as const,
};

export function useLearnerForm(options: { enabled?: boolean } = {}) {
  return useQuery<LearnerFormResponse>({
    queryKey: learnerFormKeys.me(),
    queryFn: () => api.get<LearnerFormResponse>("/me/learner-form"),
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useUpdateLearnerForm() {
  const qc = useQueryClient();
  return useMutation<LearnerFormResponse, Error, LearnerFormUpdateRequest>({
    mutationKey: ["learner-form", "update"],
    mutationFn: (body) => api.put<LearnerFormResponse>("/me/learner-form", body),
    onSuccess: (form) => {
      qc.setQueryData(learnerFormKeys.me(), form);
    },
  });
}
